import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "../components/ui/dialog";
import {
  Plus, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, Flag, Shield, Eye
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadge = {
  draft: { label: "Borrador", cls: "bg-slate-700 text-slate-300" },
  pending_review: { label: "En Revision", cls: "bg-amber-500/10 text-amber-400 border border-amber-500/20" },
  approved: { label: "Aprobada", cls: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" },
  rejected: { label: "Rechazada", cls: "bg-red-500/10 text-red-400 border border-red-500/20" },
  flagged: { label: "Sospechosa", cls: "bg-red-500/10 text-red-400 border border-red-500/20" },
};

export default function Operations() {
  const [searchParams] = useSearchParams();
  const [operations, setOperations] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [opTypes, setOpTypes] = useState([]);
  const [businessLine, setBusinessLine] = useState(null);
  const [newOp, setNewOp] = useState({
    customer_id: "", operation_type: "", operation_date: "", amount_mxn: "", payment_method: "transfer", description: "",
  });
  const [creating, setCreating] = useState(false);
  const [selectedTypeDetail, setSelectedTypeDetail] = useState(null);
  const [extraFields, setExtraFields] = useState([]);
  const [extraData, setExtraData] = useState({});

  const fetchOps = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (searchParams.get("is_suspicious") === "true") params.is_suspicious = true;
      const res = await axios.get(`${API}/operations`, { params, withCredentials: true });
      setOperations(res.data.items);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      setOperations([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, searchParams]);

  useEffect(() => { fetchOps(); }, [fetchOps]);

  // Load operation types and extra fields on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/operations/types/list`, { withCredentials: true });
        setOpTypes(res.data.types || []);
        setBusinessLine(res.data.business_line);
        // Load extra fields for the vertical
        if (res.data.business_line) {
          const ef = await axios.get(`${API}/operations/extra-fields/${res.data.business_line}`, { withCredentials: true });
          setExtraFields(ef.data.fields || []);
        }
      } catch {}
    })();
  }, []);

  const fetchCustomers = async () => {
    const res = await axios.get(`${API}/customers?limit=100`, { withCredentials: true });
    setCustomers(res.data.items);
  };

  const handleTypeChange = (typeId) => {
    setNewOp({ ...newOp, operation_type: typeId });
    const detail = opTypes.find((t) => t.id === typeId);
    setSelectedTypeDetail(detail || null);
  };

  const handleCreate = async () => {
    if (!newOp.customer_id || !newOp.amount_mxn || !newOp.operation_date || !newOp.operation_type) {
      toast.error("Completa todos los campos requeridos");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/operations`, {
        ...newOp, amount_mxn: parseFloat(newOp.amount_mxn), extra_data: extraData,
      }, { withCredentials: true });
      const data = res.data;

      if (data.structuring_flag) {
        toast.error("Posible fraccionamiento detectado. Se genero alerta critica.", { duration: 8000 });
      } else if (data.is_aml_sensitive) {
        toast.warning("Actividad Vulnerable detectada. Se requiere KYC reforzado y documentacion adicional.", { duration: 6000 });
      } else if (data.exceeds_threshold) {
        toast.warning("Operacion excede umbral AML. Se genero alerta.", { duration: 5000 });
      } else {
        toast.success("Operacion creada exitosamente");
      }

      if (data.risk_level === "critical" || data.risk_level === "high") {
        toast.warning(`Riesgo ${data.risk_level.toUpperCase()}: ${data.risk_factors?.slice(0,2).join(", ")}`, { duration: 6000 });
      }

      setShowCreate(false);
      setNewOp({ customer_id: "", operation_type: "", operation_date: "", amount_mxn: "", payment_method: "transfer", description: "" });
      setSelectedTypeDetail(null);
      setExtraData({});
      fetchOps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear operacion");
    } finally {
      setCreating(false);
    }
  };

  const handleReview = async (opId, action, notes = "") => {
    try {
      await axios.post(`${API}/operations/${opId}/review`, { action, notes }, { withCredentials: true });
      toast.success(`Operacion ${action === "approve" ? "aprobada" : action === "flag" ? "marcada sospechosa" : "rechazada"}`);
      fetchOps();
    } catch {
      toast.error("Error al revisar operacion");
    }
  };

  const handleSubmit = async (opId) => {
    try {
      await axios.post(`${API}/operations/${opId}/submit`, {}, { withCredentials: true });
      toast.success("Operacion enviada a revision");
      fetchOps();
    } catch {
      toast.error("Error al enviar operacion");
    }
  };

  const fieldClass = "bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500";

  return (
    <Layout>
      <div data-testid="operations-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="operations-heading">Operaciones</h1>
            <p className="text-slate-400 mt-1">
              {total} operaciones registradas
              {businessLine && <span className="text-teal-400 ml-2">| Vertical: {businessLine}</span>}
            </p>
          </div>
          <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (open) fetchCustomers(); }}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="new-operation-button">
                <Plus size={16} className="mr-2" /> Nueva Operacion
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle>Nueva Operacion</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-300 text-sm">Cliente *</Label>
                  <Select value={newOp.customer_id} onValueChange={(v) => setNewOp({...newOp, customer_id: v})}>
                    <SelectTrigger className={fieldClass} data-testid="op-customer-select"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                      {customers.map((c) => (
                        <SelectItem key={c.customer_id} value={c.customer_id}>
                          {c.customer_number} — {c.first_name || c.legal_name} {c.paternal_surname || ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Tipo de Operacion *</Label>
                  <Select value={newOp.operation_type} onValueChange={handleTypeChange}>
                    <SelectTrigger className={fieldClass} data-testid="op-type-select"><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-72">
                      {opTypes.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          <span className="flex items-center gap-2">
                            {t.is_aml_sensitive && <Shield size={12} className="text-red-400" />}
                            {t.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* AML Sensitive Warning */}
                {selectedTypeDetail?.is_aml_sensitive && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="aml-warning">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle size={14} className="text-red-400" />
                      <span className="text-sm font-medium text-red-400">Actividad Vulnerable (AML)</span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Esta operacion activa controles de cumplimiento reforzados: KYC mejorado, documentacion adicional y validacion de umbral.
                    </p>
                    {selectedTypeDetail.required_docs?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500 mb-1">Documentos requeridos:</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedTypeDetail.required_docs.map((d) => (
                            <Badge key={d} className="bg-slate-800 text-slate-300 text-[10px]">{d.replace(/_/g, " ")}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Vertical-specific extra fields */}
                {extraFields.length > 0 && (
                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 space-y-3" data-testid="extra-fields-section">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Datos Especificos del Giro</p>
                    <div className="grid grid-cols-2 gap-3">
                      {extraFields.map((f) => (
                        <div key={f.key} className={f.type === "text" && f.key.includes("description") ? "col-span-2" : ""}>
                          <Label className="text-slate-400 text-xs">{f.label}{f.required ? " *" : ""}</Label>
                          {f.type === "select" ? (
                            <Select value={extraData[f.key] || ""} onValueChange={(v) => setExtraData({...extraData, [f.key]: v})}>
                              <SelectTrigger className={fieldClass + " h-8 text-xs"}>
                                <SelectValue placeholder="Seleccionar..." />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                {f.options?.map((o) => (
                                  <SelectItem key={o} value={o}>{o}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              type={f.type === "number" ? "number" : "text"}
                              className={fieldClass + " h-8 text-xs"}
                              value={extraData[f.key] || ""}
                              onChange={(e) => setExtraData({...extraData, [f.key]: e.target.value})}
                              data-testid={`extra-${f.key}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-sm">Fecha *</Label>
                    <Input type="date" value={newOp.operation_date} onChange={(e) => setNewOp({...newOp, operation_date: e.target.value})} className={fieldClass} data-testid="op-date-input" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Monto MXN *</Label>
                    <Input type="number" step="0.01" value={newOp.amount_mxn} onChange={(e) => setNewOp({...newOp, amount_mxn: e.target.value})} placeholder="0.00" className={fieldClass} data-testid="op-amount-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Metodo de Pago</Label>
                  <Select value={newOp.payment_method} onValueChange={(v) => setNewOp({...newOp, payment_method: v})}>
                    <SelectTrigger className={fieldClass}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="transfer">Transferencia</SelectItem>
                      <SelectItem value="cash">Efectivo</SelectItem>
                      <SelectItem value="check">Cheque</SelectItem>
                      <SelectItem value="card">Tarjeta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Descripcion</Label>
                  <Input value={newOp.description} onChange={(e) => setNewOp({...newOp, description: e.target.value})} className={fieldClass} data-testid="op-description-input" />
                </div>
                <Button onClick={handleCreate} disabled={creating} className="w-full bg-teal-600 hover:bg-teal-500 text-white" data-testid="create-operation-submit">
                  {creating ? "Creando..." : "Crear Operacion"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700 text-slate-300" data-testid="status-filter">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="draft">Borrador</SelectItem>
              <SelectItem value="pending_review">En Revision</SelectItem>
              <SelectItem value="approved">Aprobada</SelectItem>
              <SelectItem value="rejected">Rechazada</SelectItem>
              <SelectItem value="flagged">Sospechosa</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-testid="operations-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800 text-left">
                <th className="px-5 py-3 text-xs font-medium text-slate-400 uppercase">No.</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-400 uppercase">Cliente</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-400 uppercase">Tipo</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-400 uppercase">Monto MXN</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-400 uppercase">UDI</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-400 uppercase">Estado</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-400 uppercase">AML</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-400 uppercase">Riesgo</th>
                <th className="px-5 py-3 text-xs font-medium text-slate-400 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={9} className="px-5 py-12 text-center">
                  <div className="w-6 h-6 border-2 border-teal-400 border-t-transparent rounded-full animate-spin mx-auto" />
                </td></tr>
              ) : operations.length === 0 ? (
                <tr><td colSpan={9} className="px-5 py-12 text-center text-slate-500">No hay operaciones</td></tr>
              ) : operations.map((op) => {
                const st = statusBadge[op.status] || statusBadge.draft;
                return (
                  <tr key={op.operation_id} className="hover:bg-slate-800/50" data-testid={`op-row-${op.operation_id}`}>
                    <td className="px-5 py-3 text-sm text-teal-400 font-mono">{op.operation_number}</td>
                    <td className="px-5 py-3 text-sm text-slate-200">{op.customer_name || "\u2014"}</td>
                    <td className="px-5 py-3 text-sm text-slate-300">
                      <div className="flex items-center gap-1.5">
                        {op.is_aml_sensitive && <Shield size={12} className="text-red-400" />}
                        {op.operation_type_name || op.operation_type}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-200 font-mono">
                      ${Number(op.amount_mxn).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-400 font-mono">{op.amount_udi?.toFixed(0) || "\u2014"}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex gap-1">
                        {op.is_aml_sensitive && (
                          <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px]">
                            <Shield size={10} className="mr-0.5" /> Vulnerable
                          </Badge>
                        )}
                        {op.exceeds_threshold && (
                          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px]">
                            <AlertTriangle size={10} className="mr-0.5" /> Excede
                          </Badge>
                        )}
                        {op.structuring_flag && (
                          <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-[10px]">
                            Fraccionamiento
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {op.risk_level && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          op.risk_level === "critical" ? "bg-red-500/10 text-red-400" :
                          op.risk_level === "high" ? "bg-orange-500/10 text-orange-400" :
                          op.risk_level === "medium" ? "bg-amber-500/10 text-amber-400" :
                          "bg-slate-700 text-slate-400"
                        }`}>
                          {op.risk_score || 0}/{op.risk_level}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex gap-1">
                        <Link to={`/operations/${op.operation_id}`}>
                          <Button size="sm" variant="ghost" className="text-teal-400 hover:text-teal-300 h-7 px-2 text-xs" data-testid={`detail-op-${op.operation_id}`}>
                            <Eye size={14} className="mr-1" /> Detalle
                          </Button>
                        </Link>
                        {op.status === "draft" && (
                          <Button size="sm" variant="ghost" onClick={() => handleSubmit(op.operation_id)}
                            className="text-teal-400 hover:text-teal-300 h-7 px-2 text-xs" data-testid={`submit-op-${op.operation_id}`}>
                            Enviar
                          </Button>
                        )}
                        {op.status === "pending_review" && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => handleReview(op.operation_id, "approve")}
                              className="text-emerald-400 hover:text-emerald-300 h-7 px-2 text-xs" data-testid={`approve-op-${op.operation_id}`}>
                              <CheckCircle size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleReview(op.operation_id, "flag", "Actividad sospechosa detectada")}
                              className="text-red-400 hover:text-red-300 h-7 px-2 text-xs" data-testid={`flag-op-${op.operation_id}`}>
                              <Flag size={14} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-slate-500">Pagina {page} de {pages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-slate-700 text-slate-400">
                <ChevronLeft size={16} />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} className="border-slate-700 text-slate-400">
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
